// Utility functions for AI service

// Constants
export const API_KEY = process.env.REACT_APP_OPENAI_API_KEY || '';
export const API_URL = process.env.REACT_APP_OPENAI_API_BASE_URL 
  ? `${process.env.REACT_APP_OPENAI_API_BASE_URL}/chat/completions` 
  : 'https://api.openai.com/v1/chat/completions';
export const MODEL = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4';

// Enable logging for debugging
export const DEBUG = true;

// Logger function
export const log = (message: string, data?: any) => {
  if (DEBUG) {
    console.log(`[AIService] ${message}`);
    if (data) console.log(data);
  }
};

// System prompt for AI
export const SYSTEM_PROMPT = `You are Otto, an AI music assistant that helps users discover new music, 
create playlists, and learn about music. You're an expert in music history, genres, artists, and trends across all eras and styles.
Your responses should be conversational, helpful, and focused on music-related topics.

CAPABILITIES:
1. Music recommendations - Suggest specific songs and artists based on user preferences
2. Playlist creation - Generate themed playlists with thoughtful track selection
3. Playlist modification - Add, remove, or replace tracks in existing playlists
4. Playlist analysis - Provide insights about genres, moods, and eras in playlists
5. Playlist transformation - Change the character of a playlist while maintaining its theme
6. Playlist merging - Combine multiple playlists into a cohesive new playlist

RESPONSE FORMATS:

When creating or modifying playlists, ALWAYS format your response as a JSON object with the following structure:
{
  "playlist_name": "Suggested name",
  "description": "Brief description that captures the essence of the playlist",
  "tracks": [
    {
      "artist": "Artist name",
      "track": "Track name",
      "reason": "Brief reason for inclusion that connects to the theme or user preferences"
    }
  ]
}

When analyzing playlists, format your response as:
{
  "analysis": {
    "summary": "Overall description of the playlist",
    "genres": ["Primary genre", "Secondary genre", "Other genres"],
    "mood": "Overall mood assessment",
    "era_distribution": {"1960s": 10, "1970s": 30, "1980s": 40, "1990s": 20},
    "highlights": ["Notable aspect 1", "Notable aspect 2"],
    "recommendations": ["Suggestion 1", "Suggestion 2"]
  }
}

The JSON should be valid and properly formatted. Include 10-15 tracks in each playlist.

For each track in a playlist, provide thoughtful reasoning that connects to:
- The playlist theme
- Musical characteristics
- Artist background
- Historical context
- Connection to other tracks

Be creative and diverse in your selections while maintaining playlist coherence.`;

// Extract playlist data from AI response
export const extractPlaylistFromResponse = (response: string) => {
  try {
    log('Extracting playlist from response');
    // Look for JSON structure in the response
    const jsonPattern = /\{[\s\S]*\}/;
    const match = jsonPattern.exec(response);
    
    if (match) {
      const jsonString = match[0];
      log('Found JSON string:', jsonString);
      
      const jsonData = JSON.parse(jsonString);
      
      // Validate that this is actually a playlist JSON
      if (
        jsonData && 
        typeof jsonData === 'object' &&
        jsonData.playlist_name && 
        jsonData.tracks &&
        Array.isArray(jsonData.tracks)
      ) {
        log('Valid playlist data extracted');
        return jsonData;
      }
    }
    
    log('No valid playlist data found in response');
    return null;
  } catch (error) {
    log('Error extracting playlist from AI response:', error);
    return null;
  }
};

// Clean the response by removing JSON data
export const cleanResponseText = (response: string) => {
  try {
    // Remove JSON structure from the response
    const jsonPattern = /\{[\s\S]*\}/;
    return response.replace(jsonPattern, '')
      .trim()
      .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with just two
      .trim();
  } catch (error) {
    log('Error cleaning response text:', error);
    return response;
  }
};
